import { useEffect, useRef } from 'react';
import { useAuth } from '@/zustand/auth-store';
import { useUserType } from '@/zustand/user-type-store';
import { USER_TYPES } from '@/constants/constants';
import { useGetHomePatient } from '@/apis/homePatient/query';
import { mapHomePatientResponse } from '@/pages/home/mapHomePatientResponse';
import { trackEvent, setTrackingContext } from '@/services/analytics';

export function useRetentionSignal(identityReady) {
  const { accessToken, user } = useAuth();
  const userType = useUserType(state => state.userType);
  const isPatient = userType === USER_TYPES.PATIENT;
  const firedRef = useRef(false);

  const { data: homePatientData } = useGetHomePatient({ enabled: !!accessToken && isPatient });

  useEffect(() => {
    if (!accessToken) {
      firedRef.current = false;
      return;
    }
    if (!identityReady || !isPatient || !homePatientData || firedRef.current) {
      return;
    }
    const mapped = mapHomePatientResponse(homePatientData);
    if (!mapped) {
      return;
    }

    const therapist = mapped.prescription?.therapist ?? null;
    const therapistName = therapist
      ? therapist.name ||
        [therapist.firstName ?? therapist.first_name, therapist.lastName ?? therapist.last_name]
          .filter(Boolean)
          .join(' ') ||
        null
      : null;
    const patientName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

    setTrackingContext({
      patient: user?.id,
      therapist: therapist?.id,
      prescription: mapped.prescription.id,
    });

    firedRef.current = true;
    if (mapped.progressTracker?.prescriptionStatus !== 'COMPLETED') {
      trackEvent('RETENTION_SIGNAL', {
        week_no: mapped.prescriptionWeek,
        prescription_duration: mapped.prescriptionDuration,
        frequency_a_day: mapped.progressTracker?.frequency?.day,
        frequency_a_week: mapped.progressTracker?.frequency?.week,
      });
    }
  }, [accessToken, identityReady, isPatient, homePatientData, user]);
}
